import React, { useState, useEffect, useCallback } from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import moment from 'moment';
import { useGetList } from 'react-admin';
import { cn } from '../lib/utils';
import { DeliveryAddress, DeliveryMethod, Partner } from '../types';
import { PICKUP_DATE_TBD } from '../deliveries/pickupDateTbd';
import { PickupDayRuleNote } from '../deliveries/PickupDayRuleNote';
import { pickupDayTooEarly } from '../deliveries/pickupDayRule';
import { OfficePickupRule } from '../deliveries/remainsApi';

// Types for recipient selection
export type RecipientType = 'primary' | 'authorized' | 'payer' | 'custom' | '';

/** What the family was told, when the delivery is already confirmed with them. */
export interface ConfirmedDelivery {
  method?: string | null;
  day?: string | null;
  window?: string | null;
}

/** The editor's one-line status: the next thing missing, or what's set. */
export interface DeliveryStatus {
  missing: string | null;
  summary: string;
}

export interface DeliveryAddressEditorProps {
  // Current values
  deliveryAddress?: DeliveryAddress | null;
  deliveryDate?: moment.Moment | null;
  deliveryWindow?: string;
  deliveryMethod?: DeliveryMethod | '';
  /** Where the family collects. Only meaningful for the pickup methods. */
  pickupLocationId?: string | null;
  /**
   * Decides which kind of pickup "Pickup" means: Quebec collects at our office,
   * Ontario at a Southwest location. Staff pick the place, not the category.
   */
  region?: 'ontario' | 'quebec' | null;
  deliveryNote?: string;
  /**
   * Set when the family has already been told a day. Editing then shows what
   * they were told, so a changed day is never moved silently.
   */
  confirmedDelivery?: ConfirmedDelivery | null;

  // Client IDs for auto-fill
  primaryClientId?: string | number | null;
  authorizedClientId?: string | number | null;
  payerClientId?: string | number | null;

  // Callbacks
  onAddressChange: (address: DeliveryAddress | null) => void;
  onDateChange?: (date: moment.Moment | null) => void;
  onWindowChange?: (window: string) => void;
  onMethodChange?: (method: DeliveryMethod | '') => void;
  onPickupLocationChange?: (partnerId: string | null) => void;
  onNoteChange?: (note: string) => void;
  onRecipientTypeChange?: (type: RecipientType) => void;
  onRecipientNameChange?: (name: string) => void;
  onRecipientPhoneChange?: (phone: string) => void;
  /** The earliest office pickup (T-230), with the day already on the case. */
  officePickupRule?: OfficePickupRule | null;
  savedDay?: string | null;
  overrideReason?: string;
  onOverrideReasonChange?: (reason: string) => void;
  /** Reports the status line, for a caller that shows it in its own footer. */
  onStatusChange?: (status: DeliveryStatus) => void;
  /** Leave the status line to the caller (see onStatusChange). */
  hideStatusLine?: boolean;

  // Data provider for fetching client data
  fetchClientData?: (clientId: string | number) => Promise<{
    name?: string;
    first_name?: string;
    last_name?: string;
    phone_number?: string;
    civic_number?: string;
    address?: string;
    apartment?: string;
    city?: string;
    province?: string;
    postal_code?: string;
  } | null>;
}

const WINDOWS: { value: string; label: string }[] = [
  { value: '12pm-3pm', label: '12–3 pm' },
  { value: '1pm-4pm', label: '1–4 pm' },
  { value: '2pm-5pm', label: '2–5 pm' },
];

const windowLabel = (w?: string | null): string => {
  if (!w) return '';
  if (w === '11am-2pm') return '11–2 pm';
  return WINDOWS.find((x) => x.value === w)?.label ?? w;
};

const isPickupMethod = (m?: string | null) =>
  m === 'office_pickup' || m === 'crematorium_pickup' || m === 'pickup';

const dayLabel = (d?: string | null) =>
  d ? moment(d).format('ddd, MMM D') : '';

/**
 * The one thing still missing before this delivery can go ahead, worded as the
 * next step. Shared so a caller's Save button and the editor's own status line
 * agree on what "complete" means.
 */
export const deliveryMissing = (v: {
  method?: string | null;
  day?: string | null;
  window?: string | null;
  pickupLocationId?: string | null;
  address?: DeliveryAddress | null;
}): string | null => {
  if (!v.method) return 'Choose how the ashes get home';
  // A pickup needs a place but not yet a day: the family often hasn't
  // decided, and a pickup sends them nothing until one is set (T-175).
  if (isPickupMethod(v.method)) {
    return v.pickupLocationId ? null : 'Choose where the family collects';
  }
  if (!v.day) return 'Choose a day';
  if ((v.method === 'courier' || v.method === 'hand_delivery') && !v.window) {
    return 'Choose a delivery window';
  }
  const a = v.address;
  if (!a?.selection) return 'Choose who receives the ashes';
  if (a.selection === 'custom' && !a.recipient_name?.trim()) {
    return 'Add the recipient’s name';
  }
  if (!a.postal_code && !a.formatted_address && !a.address) {
    return 'Add a delivery address';
  }
  return null;
};

// ─── Small pieces ────────────────────────────────────────────────────────────

const Icon = {
  courier: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7" />
      <circle cx="7" cy="17.5" r="1.6" />
      <circle cx="17" cy="17.5" r="1.6" />
    </svg>
  ),
  shipping: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 8l-9-5-9 5v8l9 5 9-5z" />
      <path d="M3 8l9 5 9-5M12 13v8" />
    </svg>
  ),
  pickup: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 10l1.5-5h13L20 10M4 10h16v10H4zM9 20v-5h6v5" />
    </svg>
  ),
  plus: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  warn: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 mt-px text-amber-700">
      <path d="M12 3l9.5 17h-19z" />
      <path d="M12 10v4M12 17.5v.01" />
    </svg>
  ),
  pin: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </svg>
  ),
};

const Section: React.FC<{
  label: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, htmlFor, required, children }) => (
  <section className="flex flex-col gap-2.5">
    <div className="flex items-baseline justify-between gap-2">
      {htmlFor ? (
        <label htmlFor={htmlFor} className="text-[13px] font-medium text-foreground">
          {label}
        </label>
      ) : (
        <span className="text-[13px] font-medium text-foreground">{label}</span>
      )}
      {required && <span className="text-[12px] font-medium text-amber-700">Required</span>}
    </div>
    {children}
  </section>
);

const chipClass = (selected: boolean, pill = false) =>
  cn(
    'h-10 px-3 border text-[13px] font-medium transition-colors',
    pill ? 'h-[34px] rounded-full' : 'rounded-lg',
    selected
      ? 'bg-foreground text-background border-foreground'
      : 'bg-background text-foreground border-input hover:bg-muted',
  );

const fieldClass =
  'h-10 w-full rounded-lg border border-input bg-background px-3 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40';

const formatAddress = (a: DeliveryAddress | null): string | null => {
  if (!a) return null;
  const street: string[] = [];
  if (a.apartment && a.civic_number) street.push(`${a.apartment}-${a.civic_number}`);
  else if (a.apartment) street.push(a.apartment);
  else if (a.civic_number) street.push(a.civic_number);
  if (a.address) street.push(a.address);
  const parts: string[] = [];
  if (street.length) parts.push(street.join(' '));
  if (a.city) parts.push(a.city);
  if (a.province) parts.push(a.province);
  if (a.postal_code) parts.push(a.postal_code);
  return parts.join(', ') || a.formatted_address || null;
};

// ─── Editor ──────────────────────────────────────────────────────────────────

export const DeliveryAddressEditor: React.FC<DeliveryAddressEditorProps> = ({
  deliveryAddress,
  deliveryDate,
  deliveryWindow = '',
  deliveryMethod = '',
  pickupLocationId = null,
  region = null,
  deliveryNote = '',
  confirmedDelivery = null,
  primaryClientId,
  authorizedClientId,
  payerClientId,
  onAddressChange,
  onDateChange,
  onWindowChange,
  onMethodChange,
  onPickupLocationChange,
  onNoteChange,
  onRecipientTypeChange,
  onRecipientNameChange,
  onRecipientPhoneChange,
  onStatusChange,
  hideStatusLine = false,
  fetchClientData,
  officePickupRule = null,
  savedDay = null,
  overrideReason = '',
  onOverrideReasonChange,
}) => {
  const [allowEarlierDay, setAllowEarlierDay] = useState(false);
  const [recipientType, setRecipientType] = useState<RecipientType>(
    (deliveryAddress?.selection as RecipientType) || '',
  );
  const [recipientName, setRecipientName] = useState(deliveryAddress?.recipient_name || '');
  const [recipientPhone, setRecipientPhone] = useState(deliveryAddress?.recipient_phone || '');
  const [localDeliveryMethod, setLocalDeliveryMethod] = useState(deliveryMethod);
  const [localDeliveryWindow, setLocalDeliveryWindow] = useState(deliveryWindow);
  const [localDeliveryNote, setLocalDeliveryNote] = useState(deliveryNote);
  const [localDeliveryDate, setLocalDeliveryDate] = useState<moment.Moment | null>(deliveryDate || null);
  const [addressSelect, setAddressSelect] = useState<any>(null);
  const [localAddress, setLocalAddress] = useState<DeliveryAddress | null>(deliveryAddress || null);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [noteOpen, setNoteOpen] = useState(!!deliveryNote);

  const isPickup = isPickupMethod(localDeliveryMethod);
  const usesWindow = localDeliveryMethod === 'courier' || localDeliveryMethod === 'hand_delivery';

  /**
   * One "Pickup" option, not two. Which kind it is follows the region — Quebec
   * collects at our office, Ontario at a Southwest location — and the location
   * list below is where staff make the choice that actually matters. The two
   * values stay distinct in the data because the work behind them differs: an
   * office pickup needs a courier leg to Maisonneuve, a crematorium pickup
   * needs a note to Southwest.
   */
  const pickupMethodValue: DeliveryMethod =
    localDeliveryMethod === 'office_pickup' || localDeliveryMethod === 'crematorium_pickup'
      ? localDeliveryMethod
      : region === 'ontario'
        ? 'crematorium_pickup'
        : 'office_pickup';

  // Pickup points are records now — our office, and the Southwest locations —
  // instead of a hardcoded address and a line of free text in delivery_note.
  const { data: pickupLocations = [] } = useGetList(
    'partners',
    {
      filter: { is_ashes_pickup_location: true },
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'name', order: 'ASC' },
    },
    { enabled: isPickup },
  );

  // Narrowed to the case's market, because offering an Ontario family a drive
  // to Montréal is not a choice — it's a mistake waiting to be clicked. A
  // partner with no region is shown everywhere, which is the convention the
  // partners admin documents for every other funeral dropdown.
  const pickupCandidates = (pickupLocations as Partner[]).filter(
    (p) => !p.region || p.region === region,
  );

  // Sync external props to local state
  useEffect(() => {
    if (deliveryAddress) {
      setRecipientType((deliveryAddress.selection as RecipientType) || '');
      setRecipientName(deliveryAddress.recipient_name || '');
      setRecipientPhone(deliveryAddress.recipient_phone || '');
    }
    setLocalAddress(deliveryAddress || null);
  }, [deliveryAddress]);

  useEffect(() => {
    setLocalDeliveryMethod(deliveryMethod);
  }, [deliveryMethod]);

  // One candidate is not a decision. Quebec has exactly one pickup point — our
  // own office — so preselect it rather than making staff choose from a list of
  // one and leaving the column null when they don't.
  useEffect(() => {
    if (!isPickup || pickupLocationId || !onPickupLocationChange) return;
    if (pickupCandidates.length === 1) {
      onPickupLocationChange(String(pickupCandidates[0].id));
    }
  }, [isPickup, pickupLocationId, pickupCandidates, onPickupLocationChange]);

  useEffect(() => {
    setLocalDeliveryWindow(deliveryWindow);
  }, [deliveryWindow]);

  useEffect(() => {
    setLocalDeliveryNote(deliveryNote);
    if (deliveryNote) setNoteOpen(true);
  }, [deliveryNote]);

  useEffect(() => {
    setLocalDeliveryDate(deliveryDate || null);
  }, [deliveryDate]);

  const handleRecipientTypeChange = useCallback(async (type: RecipientType) => {
    setRecipientType(type);
    onRecipientTypeChange?.(type);

    if (type === 'custom') {
      // Clear fields for custom entry but preserve selection type
      setRecipientName('');
      setRecipientPhone('');
      const customAddress: DeliveryAddress = { selection: 'custom' };
      setLocalAddress(customAddress);
      onRecipientNameChange?.('');
      onRecipientPhoneChange?.('');
      onAddressChange(customAddress);
      setIsEditingAddress(true);
      return;
    }

    let clientId: string | number | null = null;
    if (type === 'primary') clientId = primaryClientId || null;
    else if (type === 'authorized') clientId = authorizedClientId || null;
    else if (type === 'payer') clientId = payerClientId || null;

    if (clientId && fetchClientData) {
      setIsLoadingClient(true);
      try {
        const client = await fetchClientData(clientId);
        if (client) {
          const name = client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim();
          setRecipientName(name);
          setRecipientPhone(client.phone_number || '');
          onRecipientNameChange?.(name);
          onRecipientPhoneChange?.(client.phone_number || '');

          // Use client's individual address fields (not formatted_address)
          const newAddress: DeliveryAddress = {
            civic_number: client.civic_number,
            address: client.address,
            apartment: client.apartment,
            city: client.city,
            province: client.province,
            postal_code: client.postal_code,
            selection: type,
            recipient_name: name,
            recipient_phone: client.phone_number,
          };
          setLocalAddress(newAddress);
          onAddressChange(newAddress);
          setIsEditingAddress(false);
          setAddressSelect(null);
        }
      } catch (error) {
        console.error('Error fetching client data:', error);
      } finally {
        setIsLoadingClient(false);
      }
    }
  }, [primaryClientId, authorizedClientId, payerClientId, fetchClientData, onAddressChange, onRecipientTypeChange, onRecipientNameChange, onRecipientPhoneChange]);

  // Handle Google Places selection
  const handlePlaceSelect = useCallback((place: any) => {
    setAddressSelect(place);
    if (!place) return;

    const finish = (newAddress: DeliveryAddress) => {
      setLocalAddress(newAddress);
      onAddressChange(newAddress);
      // Keep editing open for custom recipients so they can add apartment
      if (recipientType !== 'custom') setIsEditingAddress(false);
      setAddressSelect(null);
    };

    if (window.google?.maps?.places) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails(
        {
          placeId: place.value.place_id,
          fields: ['address_components', 'formatted_address', 'name'],
        },
        (result: any, status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && result) {
            const newAddress: DeliveryAddress = {
              formatted_address: result.formatted_address || place.label,
              selection: recipientType || 'custom',
              recipient_name: recipientName,
              recipient_phone: recipientPhone,
            };
            (result.address_components || []).forEach((component: any) => {
              const types = component.types;
              if (types.includes('street_number')) newAddress.civic_number = component.long_name;
              else if (types.includes('route')) newAddress.address = component.long_name;
              else if (types.includes('locality') || types.includes('sublocality_level_1')) newAddress.city = component.long_name;
              else if (types.includes('administrative_area_level_1')) newAddress.province = component.short_name;
              else if (types.includes('postal_code')) newAddress.postal_code = component.long_name;
            });
            finish(newAddress);
          }
        },
      );
    } else {
      finish({
        formatted_address: place.label,
        selection: recipientType || 'custom',
        recipient_name: recipientName,
        recipient_phone: recipientPhone,
      });
    }
  }, [recipientType, recipientName, recipientPhone, onAddressChange]);

  const handleMethodChange = useCallback((method: DeliveryMethod | '') => {
    setLocalDeliveryMethod(method);
    onMethodChange?.(method);
  }, [onMethodChange]);

  const handleWindowChange = useCallback((value: string) => {
    setLocalDeliveryWindow(value);
    onWindowChange?.(value);
  }, [onWindowChange]);

  const handleDateChange = useCallback((value: string) => {
    const date = value ? moment(value, 'YYYY-MM-DD') : null;
    setLocalDeliveryDate(date);
    onDateChange?.(date);
  }, [onDateChange]);

  const handleNoteChange = useCallback((value: string) => {
    setLocalDeliveryNote(value);
    onNoteChange?.(value);
  }, [onNoteChange]);

  const handleRecipientNameChange = useCallback((value: string) => {
    setRecipientName(value);
    onRecipientNameChange?.(value);
    const updated = { ...(localAddress || { selection: 'custom' }), recipient_name: value };
    setLocalAddress(updated);
    onAddressChange(updated);
  }, [localAddress, onRecipientNameChange, onAddressChange]);

  const handleRecipientPhoneChange = useCallback((value: string) => {
    setRecipientPhone(value);
    onRecipientPhoneChange?.(value);
    const updated = { ...(localAddress || { selection: 'custom' }), recipient_phone: value };
    setLocalAddress(updated);
    onAddressChange(updated);
  }, [localAddress, onRecipientPhoneChange, onAddressChange]);

  const handleApartmentChange = (value: string) => {
    const updated: DeliveryAddress = {
      ...(localAddress || {}),
      apartment: value,
      selection: recipientType || 'custom',
      recipient_name: recipientName,
      recipient_phone: recipientPhone,
    };
    setLocalAddress(updated);
    onAddressChange(updated);
  };

  const displayAddress = formatAddress(localAddress);
  const hasAddress = !!displayAddress;
  const dayValue = localDeliveryDate ? localDeliveryDate.format('YYYY-MM-DD') : '';

  const missing = deliveryMissing({
    method: localDeliveryMethod,
    day: dayValue,
    window: localDeliveryWindow,
    pickupLocationId,
    address: localAddress
      ? { ...localAddress, selection: recipientType || localAddress.selection, recipient_name: recipientName }
      : recipientType ? { selection: recipientType, recipient_name: recipientName } : null,
  });

  const selectedLocation = pickupCandidates.find((p) => String(p.id) === String(pickupLocationId));
  const summary = (() => {
    const day = dayLabel(dayValue);
    switch (localDeliveryMethod) {
      case 'courier':
        return `Courier · ${day} · ${windowLabel(localDeliveryWindow)}`;
      case 'hand_delivery':
        return `Hand delivery · ${day} · ${windowLabel(localDeliveryWindow)}`;
      case 'shipping':
        return `Canada Post · ships ${day}`;
      default:
        return `Pickup · ${day || PICKUP_DATE_TBD}${selectedLocation ? ` · ${selectedLocation.name}` : ''}`;
    }
  })();

  useEffect(() => {
    onStatusChange?.({ missing, summary });
  }, [missing, summary, onStatusChange]);

  // Compared against what the family was told, so moving a confirmed day
  // can't happen without staff seeing it.
  const changedSinceConfirmed =
    !!confirmedDelivery &&
    ((confirmedDelivery.method ?? '') !== (localDeliveryMethod ?? '') ||
      (confirmedDelivery.day ?? '') !== dayValue ||
      (usesWindow && (confirmedDelivery.window ?? '') !== (localDeliveryWindow ?? '')));
  const toldFamily = confirmedDelivery
    ? [dayLabel(confirmedDelivery.day), windowLabel(confirmedDelivery.window)].filter(Boolean).join(' · ')
    : '';

  const methodHint =
    localDeliveryMethod === 'courier'
      ? 'Courrier Plus brings the ashes to the door.'
      : localDeliveryMethod === 'shipping'
        ? 'Shipped by Canada Post with tracking.'
        : isPickup
          ? region === 'ontario'
            ? 'The family collects at a Southwest location.'
            : 'The family collects at our Montréal office.'
          : '';

  const methods: { value: DeliveryMethod; label: string; icon: React.ReactNode; selected: boolean }[] = [
    { value: 'courier', label: 'Courier', icon: Icon.courier, selected: localDeliveryMethod === 'courier' },
    { value: 'shipping', label: 'Canada Post', icon: Icon.shipping, selected: localDeliveryMethod === 'shipping' },
    { value: pickupMethodValue, label: 'Pickup', icon: Icon.pickup, selected: isPickup && localDeliveryMethod !== 'pickup' },
  ];

  const recipients: { value: RecipientType; label: string }[] = [
    ...(primaryClientId ? [{ value: 'primary' as const, label: 'Primary client' }] : []),
    ...(authorizedClientId ? [{ value: 'authorized' as const, label: 'Authorized rep' }] : []),
    ...(payerClientId ? [{ value: 'payer' as const, label: 'Payer' }] : []),
    { value: 'custom', label: 'Someone else' },
  ];

  // The earliest-day rule follows the method being chosen here, not the saved one.
  const dayRule =
    localDeliveryMethod === 'office_pickup' && officePickupRule?.reason ? officePickupRule : null;

  const dayHeading =
    localDeliveryMethod === 'shipping' ? 'Ship day' : isPickup ? 'Pickup day' : 'Delivery day';

  return (
    <div className="flex flex-col gap-[22px] text-foreground font-sans">
      {changedSinceConfirmed && toldFamily && (
        <div role="status" className="flex gap-2.5 rounded-[10px] border border-amber-200 bg-amber-50 px-3.5 py-3 text-[13px] leading-[1.45]">
          {Icon.warn}
          <div>
            The family was told <strong>{toldFamily}</strong>. After you save, let them know about the change.
          </div>
        </div>
      )}

      {/* Method */}
      {onMethodChange && (
        <Section label="Method" required={!localDeliveryMethod}>
          <div role="radiogroup" aria-label="Delivery method" className="grid grid-cols-3 gap-2">
            {methods.map((m) => (
              <button
                key={m.label}
                type="button"
                role="radio"
                aria-checked={m.selected}
                onClick={() => handleMethodChange(m.value)}
                className={cn(
                  'h-[68px] rounded-[10px] border-[1.5px] flex flex-col items-center justify-center gap-1.5 text-[13px] transition-colors',
                  m.selected
                    ? 'border-foreground bg-muted font-semibold'
                    : 'border-border bg-background font-medium hover:bg-muted',
                )}
              >
                {m.icon}
                <span>{m.label}</span>
              </button>
            ))}
          </div>
          {localDeliveryMethod === 'hand_delivery' || localDeliveryMethod === 'pickup' ? (
            <div className="text-[12px] text-muted-foreground">
              This case uses the old {localDeliveryMethod === 'hand_delivery' ? 'Hand delivery' : 'Customer pickup'} method.
              Pick one above to move it over.
            </div>
          ) : methodHint ? (
            <div className="text-[13px] text-muted-foreground">{methodHint}</div>
          ) : null}
        </Section>
      )}

      {/* Day and window */}
      {onDateChange && (localDeliveryMethod || !onMethodChange) && (
        <Section label={dayHeading} htmlFor="delivery-day" required={!dayValue && !isPickup}>
          <div className="flex flex-wrap items-center gap-2">
            <input
              id="delivery-day"
              type="date"
              value={dayValue}
              min={dayRule && !allowEarlierDay ? dayRule.earliestDay : undefined}
              onChange={(e) => handleDateChange(e.target.value)}
              className={cn(fieldClass, 'w-auto')}
            />
            {onWindowChange && usesWindow && (
              <div role="radiogroup" aria-label="Delivery window" className="flex flex-wrap gap-1.5">
                {/* 11-2 was dropped 2026-09-11; kept for rows already on it. */}
                {localDeliveryWindow === '11am-2pm' && (
                  <button type="button" role="radio" aria-checked className={chipClass(true)}>
                    11–2 pm (old)
                  </button>
                )}
                {WINDOWS.map((w) => (
                  <button
                    key={w.value}
                    type="button"
                    role="radio"
                    aria-checked={localDeliveryWindow === w.value}
                    onClick={() => handleWindowChange(w.value)}
                    className={chipClass(localDeliveryWindow === w.value)}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            )}
            {isPickup && dayValue && (
              <button
                type="button"
                onClick={() => handleDateChange('')}
                className="text-[13px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Not known yet
              </button>
            )}
          </div>
          {dayRule && (
            <PickupDayRuleNote
              rule={dayRule}
              tooEarly={pickupDayTooEarly({
                method: localDeliveryMethod,
                day: dayValue,
                savedDay,
                rule: dayRule,
              })}
              allowEarlier={allowEarlierDay}
              onAllowEarlier={() => setAllowEarlierDay(true)}
              reason={overrideReason}
              onReasonChange={(reason) => onOverrideReasonChange?.(reason)}
            />
          )}
          {isPickup && !dayValue && (
            <div data-testid="pickup-date-tbd" className="text-[13px] text-muted-foreground">
              {PICKUP_DATE_TBD}. You can save without a day; nothing goes to the family until one is set.
            </div>
          )}
        </Section>
      )}

      {/* Recipient and address */}
      {(localDeliveryMethod || !onMethodChange) && !isPickup && (
        <Section label="Recipient" required={!recipientType}>
          <div role="radiogroup" aria-label="Recipient" className="flex flex-wrap gap-1.5">
            {recipients.map((r) => (
              <button
                key={r.value}
                type="button"
                role="radio"
                aria-checked={recipientType === r.value}
                disabled={isLoadingClient}
                onClick={() => void handleRecipientTypeChange(r.value)}
                className={chipClass(recipientType === r.value, true)}
              >
                {r.label}
              </button>
            ))}
          </div>

          {isLoadingClient && (
            <div className="text-[12px] text-muted-foreground">Loading the client’s address…</div>
          )}

          {recipientType === 'custom' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="sm:col-span-2 flex flex-col gap-1.5 min-w-0">
                <label htmlFor="recipient-name" className="text-[12px] text-muted-foreground">Name</label>
                <input
                  id="recipient-name"
                  value={recipientName}
                  onChange={(e) => handleRecipientNameChange(e.target.value)}
                  placeholder="Who receives the ashes"
                  className={fieldClass}
                />
              </div>
              <div className="flex flex-col gap-1.5 min-w-0">
                <label htmlFor="recipient-phone" className="text-[12px] text-muted-foreground">Phone</label>
                <input
                  id="recipient-phone"
                  type="tel"
                  value={recipientPhone}
                  onChange={(e) => handleRecipientPhoneChange(e.target.value)}
                  className={fieldClass}
                />
              </div>
            </div>
          )}

          {recipientType && (
            hasAddress ? (
              <div className="flex items-start gap-3 rounded-[10px] border border-border px-4 py-3.5">
                <div className="flex-1 min-w-0 flex flex-col gap-[3px]">
                  {recipientName && <div className="text-[14px] font-medium">{recipientName}</div>}
                  {recipientPhone && <div className="text-[13px] text-muted-foreground">{recipientPhone}</div>}
                  <div className="text-[13px] text-muted-foreground break-words">{displayAddress}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsEditingAddress((v) => !v)}
                  className="h-8 px-2.5 rounded-md bg-muted text-[13px] font-medium shrink-0 hover:bg-accent"
                >
                  {isEditingAddress ? 'Done' : 'Change'}
                </button>
              </div>
            ) : !isEditingAddress ? (
              <div className="flex items-center gap-3 rounded-[10px] border border-dashed border-border px-4 py-3">
                <div className="flex-1 text-[13px] text-muted-foreground">No address yet</div>
                <button
                  type="button"
                  onClick={() => setIsEditingAddress(true)}
                  className="h-8 px-2.5 rounded-md bg-muted text-[13px] font-medium shrink-0 hover:bg-accent"
                >
                  Add address
                </button>
              </div>
            ) : null
          )}

          {recipientType && isEditingAddress && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="sm:col-span-2 flex flex-col gap-1.5 min-w-0">
                <span className="text-[12px] text-muted-foreground">Address</span>
                {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
                  <GooglePlacesAutocomplete
                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                    selectProps={{
                      value: addressSelect,
                      onChange: handlePlaceSelect,
                      placeholder: 'Start typing an address',
                      isClearable: true,
                      'aria-label': 'Address',
                      styles: {
                        control: (provided: any, state: any) => ({
                          ...provided,
                          minHeight: '40px',
                          borderRadius: '0.5rem',
                          borderColor: state.isFocused ? 'hsl(147 37% 29%)' : 'hsl(147 10% 85%)',
                          boxShadow: 'none',
                          fontSize: '14px',
                          '&:hover': { borderColor: 'hsl(147 10% 70%)' },
                        }),
                        placeholder: (provided: any) => ({ ...provided, fontSize: '14px', color: 'hsl(147 5% 45%)' }),
                        input: (provided: any) => ({ ...provided, fontSize: '14px' }),
                        menu: (provided: any) => ({ ...provided, zIndex: 9999 }),
                      },
                    }}
                    autocompletionRequest={{
                      componentRestrictions: { country: ['ca', 'us'] },
                      types: ['address'],
                    }}
                  />
                ) : (
                  <input disabled placeholder="Address search isn’t set up here" className={fieldClass} />
                )}
              </div>
              <div className="flex flex-col gap-1.5 min-w-0">
                <label htmlFor="delivery-unit" className="text-[12px] text-muted-foreground">Unit</label>
                <input
                  id="delivery-unit"
                  value={localAddress?.apartment || ''}
                  onChange={(e) => handleApartmentChange(e.target.value)}
                  placeholder="Apt, suite"
                  className={fieldClass}
                />
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Pickup location. Staff-set only — the customer site never offers pickup. */}
      {onPickupLocationChange && isPickup && (
        <Section label="Pickup location" required={!pickupLocationId}>
          {pickupCandidates.length === 1 ? (
            <div className="flex items-center gap-2.5 rounded-[10px] border border-border px-4 py-3.5 text-[14px] font-medium">
              {Icon.pin}
              {pickupCandidates[0].name}
            </div>
          ) : (
            <div role="radiogroup" aria-label="Pickup location" className="flex flex-col gap-2">
              {pickupCandidates.map((p) => {
                const selected = String(p.id) === String(pickupLocationId);
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => onPickupLocationChange(String(p.id))}
                    className={cn(
                      'h-[52px] px-3.5 rounded-[10px] border-[1.5px] flex items-center gap-3 text-left text-[14px] font-medium transition-colors',
                      selected ? 'border-foreground bg-muted' : 'border-border bg-background hover:bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center shrink-0',
                        selected ? 'border-foreground' : 'border-muted-foreground',
                      )}
                    >
                      {selected && <span className="w-2 h-2 rounded-full bg-foreground" />}
                    </span>
                    {p.name}
                  </button>
                );
              })}
            </div>
          )}
          <div className="text-[13px] text-muted-foreground">
            The family is told the address when you confirm the day.
          </div>
        </Section>
      )}

      {/* Delivery note */}
      {onNoteChange && (localDeliveryMethod || !onMethodChange) && (
        noteOpen ? (
          <Section label="Delivery note" htmlFor="delivery-note">
            <textarea
              id="delivery-note"
              rows={3}
              value={localDeliveryNote}
              onChange={(e) => handleNoteChange(e.target.value)}
              placeholder="Gate code, buzzer, who to ask for"
              className="min-h-[72px] w-full rounded-lg border border-input bg-background px-3 py-2.5 text-[14px] resize-y focus:outline-none focus:ring-2 focus:ring-ring/40"
            />
          </Section>
        ) : (
          <button
            type="button"
            onClick={() => setNoteOpen(true)}
            className="self-start h-8 flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground hover:text-foreground"
          >
            {Icon.plus}
            Add delivery note
          </button>
        )
      )}

      {/* One line that says either what's set or the next thing to do. */}
      {onMethodChange && !hideStatusLine && (
        <div
          aria-live="polite"
          className={cn(
            'border-t border-border pt-3 text-[13px]',
            missing ? 'text-amber-700 font-medium' : 'text-muted-foreground',
          )}
        >
          {missing ?? summary}
        </div>
      )}
    </div>
  );
};

export default DeliveryAddressEditor;
